/*
 * <whatsapp-link> wraps a button block that opens a WhatsApp chat.
 * - Builds https://wa.me/<number>?text=<message, chosen option, product link> at click time.
 * - If data-required-option is set (e.g. "Size") and the product has that option, the customer
 *   must pick it themselves first; otherwise the click is blocked and an error is shown.
 * The theme may re-render the product form on variant change, so state lives on window
 * and events are delegated from document.
 */
(() => {
  if (customElements.get('whatsapp-link')) return;

  const chosen = (window.__whatsappChosenOptions = window.__whatsappChosenOptions || new Set());
  const keyFor = (productUrl, optionName) => `${productUrl}|${optionName.trim().toLowerCase()}`;

  // Option name from "Size:" legend/label text, ignoring the selected-value span and size chart link.
  const optionNameOf = (labelEl) => {
    if (!labelEl) return '';
    const clone = labelEl.cloneNode(true);
    clone.querySelectorAll('[data-selected-value], .variant-option__size-chart').forEach((el) => el.remove());
    return clone.textContent.replace(':', '').trim();
  };

  // Returns { exists, value } for the named option within root.
  const readOption = (root, optionName) => {
    const wanted = optionName.trim().toLowerCase();
    for (const fieldset of root.querySelectorAll('variant-selects fieldset')) {
      if (optionNameOf(fieldset.querySelector('legend')).toLowerCase() !== wanted) continue;
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      return { exists: true, value: checked ? checked.value : '' };
    }
    for (const wrapper of root.querySelectorAll('variant-selects .variant-option--dropdowns')) {
      if (optionNameOf(wrapper.querySelector('label')).toLowerCase() !== wanted) continue;
      const select = wrapper.querySelector('select');
      return { exists: true, value: select ? select.value : '' };
    }
    return { exists: false, value: '' };
  };

  // Remember options the customer picked themselves.
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof Element) || !input.matches('variant-selects input[type="radio"], variant-selects select')) return;

    const selects = input.closest('variant-selects');
    const holder = input.closest('fieldset') || input.closest('.variant-option--dropdowns');
    if (!selects || !holder) return;

    const name = optionNameOf(holder.querySelector('legend, label'));
    chosen.add(keyFor(selects.dataset.productUrl || location.pathname, name));
    document.querySelectorAll('whatsapp-link').forEach((el) => el.refresh());
  });

  class WhatsappLink extends HTMLElement {
    connectedCallback() {
      this.link = this.querySelector('a');
      this.error = this.querySelector('.whatsapp-link__error');
      this.onClick = this.onClick.bind(this);
      this.link?.addEventListener('click', this.onClick);
      this.refresh();
    }

    disconnectedCallback() {
      this.link?.removeEventListener('click', this.onClick);
    }

    get root() {
      return this.closest('.product-details') || this.closest('section') || document;
    }

    get productUrl() {
      return this.dataset.productUrl || location.pathname;
    }

    // Needs a pick when the option exists on this product and the customer has not chosen it yet.
    get needsPick() {
      const option = this.dataset.requiredOption;
      if (!option) return false;
      if (!readOption(this.root, option).exists) return false;
      return !chosen.has(keyFor(this.productUrl, option));
    }

    refresh() {
      const blocked = this.needsPick;
      this.toggleAttribute('data-blocked', blocked);
      this.link?.setAttribute('aria-disabled', String(blocked));
      if (!blocked && this.error) this.error.hidden = true;
    }

    buildUrl() {
      const parts = [this.dataset.message || ''];
      const option = this.dataset.requiredOption;
      if (option) {
        const { exists, value } = readOption(this.root, option);
        if (exists && value) parts.push(`${option}: ${value}`);
      }
      if (this.dataset.productUrl) {
        const url = new URL(this.dataset.productUrl, location.origin);
        const variantId = this.root.querySelector('form input[name="id"]')?.value;
        if (variantId) url.searchParams.set('variant', variantId);
        parts.push(url.toString());
      }
      const text = parts.map((p) => p.trim()).filter(Boolean).join('\n');
      return `https://wa.me/${this.dataset.number}${text ? `?text=${encodeURIComponent(text)}` : ''}`;
    }

    onClick(event) {
      if (this.needsPick) {
        event.preventDefault();
        this.refresh();
        if (this.error) this.error.hidden = false;
        return;
      }
      // Let the default navigation (new tab) use the freshly built link.
      this.link.href = this.buildUrl();
    }
  }

  customElements.define('whatsapp-link', WhatsappLink);
})();
